import z from "zod";

import type {
  SignupData,
  AuthResponse,
  LoginData,
  ResendVerificationData,
} from "@/schemas";
import { AuthResponseSchema } from "@/schemas";
import { SimpleResponseSchema } from "@/schemas/common";

import { apiRequest, apiRequestFormData } from "./apiUtils";

export async function signup(data: SignupData): Promise<AuthResponse> {
  const res = await apiRequest<unknown>(`auth/register`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const parsed = AuthResponseSchema.safeParse(res);
  if (parsed.success) return parsed.data;
  // Failure response: { success: false, message: "..." } — tokens are absent so Zod
  // can't parse it as a full AuthResponse. Surface the backend message instead.
  const msg =
    res && typeof res === "object" && "message" in res && typeof (res as any).message === "string"
      ? (res as any).message
      : "Registration failed. Please try again.";
  throw new Error(msg);
}

export async function login(data: LoginData): Promise<AuthResponse> {
  const res = await apiRequest<unknown>(`auth/signin`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  return AuthResponseSchema.parse(res);
}

export async function refreshToken(refreshToken: string): Promise<AuthResponse> {
  const res = await apiRequest<unknown>(`auth/refresh`, {
    method: "POST",
    body: JSON.stringify({ refreshToken }),
  });
  return AuthResponseSchema.parse(res);
}

export async function resendVerification(
  data: ResendVerificationData,
): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`verify/init`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function confirmVerification(
  data: ResendVerificationData,
): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`verify/confirm`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function updatePhone(phone: string): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`verify/phone`, {
    method: "PATCH",
    body: JSON.stringify({ phone }),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function verifyEmailByToken(
  token: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`auth/verify-email`, {
    method: "POST",
    body: JSON.stringify({ token }),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function markBusinessOnboardingComplete(): Promise<{
  success: boolean;
  message?: string;
}> {
  const res = await apiRequest<unknown>(`business/onboarding-complete`, {
    method: "GET",
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function markWorkerOnboardingComplete(): Promise<{
  success: boolean;
  message?: string;
}> {
  const res = await apiRequest<unknown>(`worker/onboarding-complete`, {
    method: "GET",
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function forgotPasswordInit(
  email: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`auth/forgot-init`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function forgotPasswordComplete(data: {
  token: string;
  newPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`auth/forgot-complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function passwordRecoveryInit(
  email: string,
): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`auth/password-recovery/init`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function passwordRecoveryComplete(data: {
  email: string;
  code: string;
  newPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`auth/password-recovery/complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function passwordChangeInit(): Promise<{
  success: boolean;
  message?: string;
}> {
  const res = await apiRequest<unknown>(`user/password/change-init`, {
    method: "POST",
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

export async function passwordChangeComplete(data: {
  code: string;
  newPassword: string;
}): Promise<{ success: boolean; message?: string }> {
  const res = await apiRequest<unknown>(`user/password/change-complete`, {
    method: "POST",
    body: JSON.stringify(data),
  });
  const parsed = SimpleResponseSchema.parse(res);
  return { success: parsed.success, message: parsed.message ?? undefined };
}

const UploadFileResponseSchema = z
  .object({
    url: z.string().optional(),
    path: z.string().optional(),
    location: z.string().optional(),
    file: z.string().optional(),
    data: z.any().optional(),
  })
  .passthrough();

function extractUrlFromResponse(data: unknown): string {
  if (!data) return "";
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const candidates = ["url", "path", "location", "file"];
    for (const key of candidates) {
      const v = obj[key];
      if (typeof v === "string" && v.length > 0) return v;
    }
    // Check nested `data` object if present
    const nested = obj["data"];
    if (nested && typeof nested === "object") {
      const nobj = nested as Record<string, unknown>;
      for (const key of candidates) {
        const v = nobj[key];
        if (typeof v === "string" && v.length > 0) return v;
      }
    }
  }
  return "";
}

export async function uploadProfileFile(file: File): Promise<{ url: string }> {
  const formData = new FormData();
  // Keep the field name as "file"
  formData.append("file", file);
  const data = await apiRequestFormData<unknown>(
    `user/profile/upload-file`,
    formData,
  );

  // Parse to known shape where possible, then extract
  const parsed = UploadFileResponseSchema.safeParse(data);
  if (parsed.success) {
    const url = extractUrlFromResponse(parsed.data);
    return { url };
  }

  // Fallback: best-effort extraction
  return { url: extractUrlFromResponse(data) };
}
